import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Heading,
  Text,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Flex,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  useDisclosure,
  VStack,
  Badge,
  Spinner,
} from '@chakra-ui/react';
import { FiPlus, FiFileText, FiCalendar, FiClock } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { projectApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Dashboard = () => {
  const { currentUser } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newProjectName, setNewProjectName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const navigate = useNavigate();
  
  // プロジェクト一覧を取得
  const fetchProjects = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await projectApi.getProjects();
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      setError('プロジェクト一覧の取得に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  };
  
  // マウント時にプロジェクト一覧を取得
  useEffect(() => {
    fetchProjects();
  }, []);
  
  // 新規プロジェクト作成
  const handleCreateProject = async () => {
    if (!newProjectName.trim()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await projectApi.createProject({
        name: newProjectName,
      });
      
      // 新しいプロジェクトを追加
      setProjects([...projects, response.data]);
      
      // モーダルを閉じる
      onClose();
      
      // 入力をクリア
      setNewProjectName('');
      
      // 新しいプロジェクトに移動
      navigate(`/project/${response.data.id}`);
    } catch (error) {
      console.error('Failed to create project:', error);
      setError('プロジェクトの作成に失敗しました。');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // プロジェクトを開く
  const handleOpenProject = (projectId) => {
    navigate(`/project/${projectId}`);
  };
  
  // プロジェクトカード間の色を交互に変える
  const getCardBgColor = (index) => {
    return index % 2 === 0
      ? useColorModeValue('gray.50', 'gray.700')
      : useColorModeValue('white', 'gray.800');
  };
  
  // プロジェクトカードのコンポーネント
  const ProjectCard = ({ project, index }) => {
    return (
      <Card
        direction="column"
        overflow="hidden"
        variant="outline"
        bg={getCardBgColor(index)}
        boxShadow="sm"
        _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
        transition="all 0.2s"
        cursor="pointer"
        onClick={() => handleOpenProject(project.id)}
      >
        <CardHeader pb={2}>
          <Heading size="md">{project.name}</Heading>
        </CardHeader>
        
        <CardBody py={2}>
          <Flex direction="column" gap={2}>
            <Flex align="center">
              <Box as={FiCalendar} mr={2} />
              <Text fontSize="sm">作成日: {new Date(project.created_at).toLocaleDateString()}</Text>
            </Flex>
            <Flex align="center">
              <Box as={FiClock} mr={2} />
              <Text fontSize="sm">更新日: {new Date(project.updated_at).toLocaleDateString()}</Text>
            </Flex>
          </Flex>
        </CardBody>
        
        <CardFooter pt={2}>
          <Badge colorScheme="blue">{project.history_count} 件の履歴</Badge>
        </CardFooter>
      </Card>
    );
  };
  
  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb={6}>
        <Heading size="lg">ダッシュボード</Heading>
        <Button
          leftIcon={<FiPlus />}
          colorScheme="brand"
          onClick={onOpen}
        >
          新規プロジェクト
        </Button>
      </Flex>
      
      {/* 統計情報 */}
      <StatGroup
        bg={useColorModeValue('white', 'gray.800')}
        p={4}
        borderRadius="lg"
        boxShadow="sm"
        mb={8}
      >
        <Stat>
          <StatLabel>プロジェクト数</StatLabel>
          <StatNumber>{projects.length}</StatNumber>
        </Stat>
        
        <Stat>
          <StatLabel>最近の更新</StatLabel>
          <StatNumber>
            {projects.length > 0
              ? new Date(
                  Math.max(...projects.map(p => new Date(p.updated_at).getTime()))
                ).toLocaleDateString()
              : '-'}
          </StatNumber>
        </Stat>
        
        <Stat>
          <StatLabel>OCR履歴</StatLabel>
          <StatNumber>
            {projects.reduce((total, p) => total + p.history_count, 0)}
          </StatNumber>
        </Stat>
      </StatGroup>
      
      {/* プロジェクト一覧 */}
      <Box mb={8}>
        <Heading size="md" mb={4}>プロジェクト一覧</Heading>
        
        {isLoading ? (
          <Flex justify="center" align="center" h="200px">
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="brand.500"
              size="xl"
            />
          </Flex>
        ) : error ? (
          <Box
            p={4}
            bg="red.50"
            color="red.500"
            borderRadius="md"
            textAlign="center"
          >
            {error}
          </Box>
        ) : projects.length === 0 ? (
          <Box
            p={8}
            bg={useColorModeValue('gray.50', 'gray.700')}
            borderRadius="md"
            textAlign="center"
          >
            <Box as={FiFileText} fontSize="3xl" mb={4} />
            <Text mb={4}>プロジェクトがありません</Text>
            <Button
              colorScheme="brand"
              size="sm"
              leftIcon={<FiPlus />}
              onClick={onOpen}
            >
              新規プロジェクト作成
            </Button>
          </Box>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {projects.map((project, index) => (
              <ProjectCard key={project.id} project={project} index={index} />
            ))}
          </SimpleGrid>
        )}
      </Box>
      
      {/* 新規プロジェクト作成モーダル */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>新規プロジェクト作成</ModalHeader>
          <ModalCloseButton />
          
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>プロジェクト名</FormLabel>
                <Input
                  placeholder="プロジェクト名を入力"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              キャンセル
            </Button>
            <Button
              colorScheme="brand"
              onClick={handleCreateProject}
              isLoading={isSubmitting}
              isDisabled={!newProjectName.trim()}
            >
              作成
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Dashboard;